import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { detectLocale, pathMap } from "@/lib/locale";

const STORAGE_KEY = "calv.consent.v1";

type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

const COPY = {
  de: {
    eyebrow: "Cookies & Datenschutz",
    title: "Wir respektieren Ihre Privatsphäre.",
    body:
      "Wir verwenden Cookies — technisch notwendige sowie optionale für Statistik und Marketing. Sie entscheiden, was geladen wird.",
    accept: "Alle akzeptieren",
    reject: "Nur notwendige",
    settings: "Einstellungen",
    save: "Auswahl speichern",
    back: "Zurück",
    necessary: "Notwendig",
    necessaryDesc: "Erforderlich für den Betrieb der Website. Immer aktiv.",
    analytics: "Statistik",
    analyticsDesc: "Anonymisierte Reichweitenmessung, um die Seite zu verbessern.",
    marketing: "Marketing",
    marketingDesc: "Tracking für Kampagnen-Auswertung und Remarketing.",
    privacy: "Datenschutzerklärung",
  },
  en: {
    eyebrow: "Cookies & privacy",
    title: "We respect your privacy.",
    body:
      "We use cookies — strictly necessary plus optional ones for analytics and marketing. You decide what loads.",
    accept: "Accept all",
    reject: "Only necessary",
    settings: "Settings",
    save: "Save selection",
    back: "Back",
    necessary: "Necessary",
    necessaryDesc: "Required to operate the website. Always on.",
    analytics: "Analytics",
    analyticsDesc: "Anonymous reach measurement to improve the site.",
    marketing: "Marketing",
    marketingDesc: "Tracking for campaign attribution and remarketing.",
    privacy: "Privacy policy",
  },
} as const;

function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Consent) : null;
  } catch {
    return null;
  }
}

function writeConsent(c: Consent) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    window.dispatchEvent(new CustomEvent("calv:consent", { detail: c }));
  } catch {
    /* ignore */
  }
}

export function CookieConsent() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(path);
  const c = COPY[locale];
  const privacyHref = locale === "en" ? pathMap.privacy.en : pathMap.privacy.de;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"banner" | "settings">("banner");
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setOpen(true);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
    const reopen = () => {
      const cur = readConsent();
      if (cur) {
        setAnalytics(cur.analytics);
        setMarketing(cur.marketing);
      }
      setView("settings");
      setOpen(true);
    };
    window.addEventListener("calv:openConsent", reopen);
    return () => window.removeEventListener("calv:openConsent", reopen);
  }, []);

  if (!open) return null;

  const save = (a: boolean, m: boolean) => {
    writeConsent({ necessary: true, analytics: a, marketing: m, ts: Date.now() });
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={c.title}
      className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-lg border border-rule bg-white p-6 shadow-2xl md:p-7">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
          {c.eyebrow}
        </div>
        <h2 className="mt-2 font-display text-xl text-ink md:text-2xl">{c.title}</h2>

        {view === "banner" ? (
          <>
            <p className="mt-3 text-sm text-ink-3 md:text-base">
              {c.body}{" "}
              <a className="text-cyan-deep underline" href={privacyHref}>
                {c.privacy}
              </a>
              .
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => save(true, true)}
                className="inline-flex items-center justify-center rounded-md bg-lime px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-lime-hover"
              >
                {c.accept}
              </button>
              <button
                onClick={() => save(false, false)}
                className="inline-flex items-center justify-center rounded-md border border-rule bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink/5"
              >
                {c.reject}
              </button>
              <button
                onClick={() => setView("settings")}
                className="inline-flex items-center justify-center rounded-md px-3 py-2.5 text-sm font-medium text-ink-3 underline-offset-4 hover:text-ink hover:underline"
              >
                {c.settings}
              </button>
            </div>
          </>
        ) : (
          <>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start justify-between gap-4 rounded-md border border-rule p-4">
                <div>
                  <div className="font-semibold text-ink">{c.necessary}</div>
                  <div className="mt-1 text-sm text-ink-3">{c.necessaryDesc}</div>
                </div>
                <input type="checkbox" checked disabled className="mt-1 h-4 w-4 accent-lime" />
              </li>
              <li className="flex items-start justify-between gap-4 rounded-md border border-rule p-4">
                <div>
                  <div className="font-semibold text-ink">{c.analytics}</div>
                  <div className="mt-1 text-sm text-ink-3">{c.analyticsDesc}</div>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-lime"
                />
              </li>
              <li className="flex items-start justify-between gap-4 rounded-md border border-rule p-4">
                <div>
                  <div className="font-semibold text-ink">{c.marketing}</div>
                  <div className="mt-1 text-sm text-ink-3">{c.marketingDesc}</div>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-lime"
                />
              </li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={() => save(analytics, marketing)}
                className="inline-flex items-center justify-center rounded-md bg-lime px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-lime-hover"
              >
                {c.save}
              </button>
              <button
                onClick={() => setView("banner")}
                className="inline-flex items-center justify-center rounded-md border border-rule bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink/5"
              >
                {c.back}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Programmatically reopen the consent settings (e.g. from footer link). */
export function openCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("calv:openConsent"));
  }
}
